
import React, { useState, useEffect, useMemo } from 'react';
import { getMissedCards, getBookmarkedCards } from '../services/studyService';
import { getAllDrugs } from '../services/pharmacyService';
import type { View, QuizMode, QuizType, QuizSettings, DrugInfo } from '../types';
import { useAuth } from '../contexts/AuthContext';

const SmallLogoIcon = () => (
  <svg width="24" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="5" fill="#FACC15"/>
    <line x1="24" y1="9" x2="24" y2="17" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="7" r="2" fill="white"/>
    <line x1="24" y1="31" x2="24" y2="39" stroke="white" strokeWidth="2"/>
    <circle cx="24" cy="41" r="2" fill="white"/>
    <line x1="9" y1="24" x2="17" y2="24" stroke="white" strokeWidth="2"/>
    <circle cx="7" cy="24" r="2" fill="white"/>
    <line x1="31" y1="24" x2="39" y2="24" stroke="white" strokeWidth="2"/>
    <circle cx="41" cy="24" r="2" fill="white"/>
  </svg>
);
const BackIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>;
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
const PrevIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>;
const NextIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>;
const StarIcon: React.FC<{ filled: boolean; className?: string }> = ({ filled, className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={`h-8 w-8 transition-colors ${className} ${filled ? 'text-yellow-400 fill-current' : 'text-gray-600 hover:text-yellow-400'}`} viewBox="0 0 20 20" fill="currentColor">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
);

const StatCard: React.FC<{ value: number; label: string }> = ({ value, label }) => (
    <div className="bg-[#15243E] p-6 rounded-xl border border-blue-900/50 text-center">
        <p className="text-4xl font-bold text-yellow-400">{value}</p>
        <p className="text-gray-400 mt-1">{label}</p>
    </div>
);

const Dropdown: React.FC<{ label: string, options: {value: string, label: string}[], value: string, onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void }> = ({ label, options, value, onChange }) => (
    <div>
        <label className="block text-sm font-medium text-gray-400 mb-2">{label}</label>
        <select value={value} onChange={onChange} className="w-full bg-[#101D35] border border-[#3A4A67] rounded-lg p-3 focus:outline-none focus:ring-1 focus:ring-yellow-400 appearance-none">
            {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
    </div>
);

interface StudyModeProps {
    onNavigate: (view: View) => void;
    onStartQuiz: (settings: QuizSettings) => void;
}

const StudyMode: React.FC<StudyModeProps> = ({ onNavigate, onStartQuiz }) => {
    const { subscription, setIsUpgradeModalOpen, uid } = useAuth();
    const [allDrugs, setAllDrugs] = useState<DrugInfo[]>([]);
    const [stats, setStats] = useState({ review: 0, missed: 0, bookmarked: 0 });
    
    const [quizMode, setQuizMode] = useState<QuizMode>('due');
    const [quizType, setQuizType] = useState<QuizType>('flashcard');

    useEffect(() => {
        if (!uid) return;
        const fetchStatsAndDrugs = async () => {
            const drugs = await getAllDrugs(uid);
            setAllDrugs(drugs);
            const missed = getMissedCards(uid);
            const bookmarked = getBookmarkedCards(uid);
            setStats({
                review: subscription?.plan === 'Free' || subscription?.plan === 'Trial' ? Math.min(drugs.length, 10) : drugs.length,
                missed: missed.length,
                bookmarked: bookmarked.length,
            });
        };
        fetchStatsAndDrugs();
    }, [subscription, uid]);
    
    const handleStart = async () => {
        if (navigator.vibrate) navigator.vibrate(10);
        
        if (subscription?.plan === 'Free' || subscription?.plan === 'Trial') {
            setIsUpgradeModalOpen(true);
            return;
        }
        if (!uid) return;

        let drugIds: string[] = [];
        switch (quizMode) {
            case 'due':
                drugIds = allDrugs.map(d => d.id);
                break;
            case 'missed':
                drugIds = getMissedCards(uid);
                break;
            case 'bookmarked':
                drugIds = getBookmarkedCards(uid);
                break;
        }

        if (drugIds.length === 0) {
            alert(`There are no cards to review in this mode.`);
            return;
        }

        // Shuffle
        for (let i = drugIds.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [drugIds[i], drugIds[j]] = [drugIds[j], drugIds[i]];
        }

        onStartQuiz({
            mode: quizMode,
            type: quizType,
            drugIds: drugIds,
        });
    };
    
    const isStartDisabled = useMemo(() => {
        if (quizMode === 'missed' && stats.missed === 0) return true;
        if (quizMode === 'bookmarked' && stats.bookmarked === 0) return true;
        if (quizMode === 'due' && stats.review === 0) return true;
        return false;
    }, [quizMode, stats]);
    
    return (
        <div className="py-6">
            <header className="text-center mb-8">
                 <div className="flex justify-between items-start mb-4">
                    <button onClick={() => onNavigate('library')} className="flex items-center text-sm text-yellow-400 hover:text-yellow-300 font-semibold">
                        <BackIcon /> Back to Library
                    </button>
                    <button onClick={() => onNavigate('dashboard')} className="flex items-center gap-2 opacity-80 hover:opacity-100 transition-all cursor-pointer hover:scale-105">
                         <SmallLogoIcon />
                         <span className="text-sm font-bold text-white">Dr.<span className="text-yellow-400">Rx</span><sup className="text-xs -top-2">TM</sup></span>
                    </button>
                </div>
                <h1 className="text-4xl font-bold">Study Mode</h1>
                {(subscription?.plan === 'Free' || subscription?.plan === 'Trial') && <p className="text-yellow-400 mt-2">Free/Trial plan is limited to Top 10 drugs. Upgrade to Plus for full library access.</p>}
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                <StatCard value={stats.review} label="Cards for Review" />
                <StatCard value={stats.missed} label="Missed Cards" />
                <StatCard value={stats.bookmarked} label="Bookmarked" />
            </div>

            <div className="bg-[#15243E] p-6 rounded-xl border border-blue-900/50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <Dropdown 
                        label="Quiz Mode" 
                        value={quizMode}
                        onChange={e => setQuizMode(e.target.value as QuizMode)}
                        options={[
                            { value: "due", label: `Review All (${stats.review} cards)` }, 
                            { value: "missed", label: `Review Missed (${stats.missed} cards)` },
                            { value: "bookmarked", label: `Review Bookmarked (${stats.bookmarked} cards)` },
                            { value: "naplex", label: "NAPLEX Practice" }
                        ]} 
                    />
                    <Dropdown 
                        label="Quiz Type"
                        value={quizType}
                        onChange={e => setQuizType(e.target.value as QuizType)}
                        options={[
                            { value: "flashcard", label: "Flashcard" },
                            { value: "mcq", label: "Multiple Choice (coming soon)" }
                        ]} 
                     />
                </div>
                <button 
                    onClick={handleStart}
                    disabled={isStartDisabled}
                    className="w-full bg-yellow-400 text-gray-900 font-bold py-4 rounded-lg hover:bg-yellow-300 transition-transform hover:scale-[1.02] disabled:bg-gray-600 disabled:text-gray-400 disabled:cursor-not-allowed disabled:scale-100"
                >
                    {(subscription?.plan === 'Free' || subscription?.plan === 'Trial') ? 'Upgrade to Study' : 'Start Quiz'}
                </button>
            </div>
        </div>
    );
};

export default StudyMode;
